// Mobil „Speed Dial" FAB (Material-minta): egyetlen kis lebegő gomb jobbra lent (kb. a hamburger-méret); kattintásra
// FELFELÉ nyílik, és felkínálja a gyors-akciókat.
//
// A SORREND FELÜLRŐL LEFELÉ: Keresés → „Kérdezd Mo-t" → az oldal MÁSODLAGOS akciói → az ELSŐDLEGES akció
// (legalul, a hüvelykujjhoz legközelebb). A tömb sorrendje ez — a lista `flex-col`-ban, a FAB fölött nyílik.
// MIÉRT ITT a másodlagos akciók: mobilon a sáv-gombok elrejtve (375 px-en a kereső + „Szűrők" az elsődleges),
// tehát a „Lekérés", „Export", „Kategóriák" és társaik KÜLÖNBEN elérhetetlenek lennének. Egységes megjelenés:
// minden akció FEHÉR körocske + címke-pill; az elsődlegest a ZÖLD (brand) ikon/felirat emeli ki. Görgetésre elrejtőzik
// (lefelé → eltűnik, felfelé → visszajön; a tartalom olvasásakor nem zavar). Csak mobilon (a hívó `md:hidden`).
import { BoltIcon } from '@heroicons/react/24/solid';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

export interface FabAction {
  key: string;
  icon: ReactNode;
  label: string;
  onClick: () => void;
  /** Kiemelt (elsődleges) akció → zöld (brand) ikon + felirat; a kör-háttér fehér, mint a többié. */
  highlight?: boolean;
}

export function MobileActionFab({ actions }: { actions: FabAction[] }): JSX.Element | null {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Görgetésre elrejtés: lefelé → eltűnik (+ becsukódik), felfelé → visszajön. Capture-listener → bármelyik belső
  // görgető-konténer (main VAGY a lista-kártya) scrolljára reagál (a scroll nem buborékol, de capture-fázisban elérjük).
  useEffect(() => {
    let last = 0;
    const onScroll = (e: Event): void => {
      const el = e.target;
      const st = el instanceof HTMLElement ? el.scrollTop : 0;
      if (st > last + 6) {
        setHidden(true);
        setOpen(false);
      } else if (st < last - 6) {
        setHidden(false);
      }
      last = st < 0 ? 0 : st;
    };
    document.addEventListener('scroll', onScroll, true);
    return () => document.removeEventListener('scroll', onScroll, true);
  }, []);

  if (actions.length === 0) return null;

  return (
    <div
      className={cn(
        // A LEBEGŐ GOMB a BIZTONSÁGI ZÓNA FÖLÖTT ül: `fixed` elem, tehát a keret safe-area paddingje NEM
        // hat rá — telefonon a home-indikátor sávjába lógott volna (`viewport-fit=cover` mellett).
        'app-fixed-bottom fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-[calc(1rem+env(safe-area-inset-right))] z-40 flex flex-col items-end transition-[transform,opacity] duration-200 md:hidden',
        hidden && 'pointer-events-none translate-y-20 opacity-0',
      )}
    >
      {/* Kattintás-kívül záró réteg — EGYBEN HALVÁNY SÖTÉTÍTÉS (user-döntés): a kinyíló akciók fehér
          körei és a pill-címkéi belevesztek a mögöttes lapba (a „Keresés vagy ugrás…" sáv és a lista-szöveg
          pont a címkék magasságában fut). A fátyol SZÁNDÉKOSAN halvány — nem modál-scrim: a lap maradjon
          olvasható, csak essen hátra egy síkkal. */}
      {open && (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => setOpen(false)}
          className="animate-in fade-in app-fixed-fill fixed inset-0 -z-10 cursor-default bg-black/35 duration-150"
        />
      )}

      {/* Kinyíló akció-sorok — egységes fehér körök; az elsődleges zöld ikonnal/felirattal. */}
      {open && (
        <div className="animate-in fade-in slide-in-from-bottom-1 mb-2.5 flex flex-col items-end gap-2 duration-150">
          {actions.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={() => {
                setOpen(false);
                a.onClick();
              }}
              className="flex items-center gap-2"
            >
              <span className="bg-card text-foreground rounded-md border px-2 py-0.5 text-xs font-medium shadow-sm">
                {a.label}
              </span>
              <span
                className={cn(
                  'bg-card flex size-9 items-center justify-center rounded-full border shadow-sm [&_svg]:size-[18px]',
                  a.highlight ? 'text-brand' : 'text-foreground',
                )}
              >
                {a.icon}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Fő FAB — kis gradiens gomb (kb. hamburger-méret); a „+" ×-re fordul nyitáskor. */}
      <button
        type="button"
        aria-expanded={open}
        aria-label={open ? t('common.close') : t('nav.quickActions')}
        onClick={() => setOpen((o) => !o)}
        className="flex size-9 items-center justify-center rounded-full bg-gradient-to-r from-[var(--accent-grad-from)] to-[var(--accent-grad-to)] text-white shadow-lg transition-transform active:scale-95"
      >
        {/* Launcher-ikon: ⚡ Bolt („gyors műveletek") — nem „+" (az létrehozást sugallna); a nyitott állapotot a
            kinyíló menü + a háttér jelzi, nincs X-morph. Alternatíva: SparklesIcon / Squares2X2Icon / ChevronUpIcon. */}
        <BoltIcon className={cn('size-5 transition-transform duration-200', open && 'scale-90')} />
      </button>
    </div>
  );
}
